import React, { FC, useState } from 'react';
import { useHistory, useLocation } from 'react-router';

import { BUTTON_TYPES, EvButton, EvConfirm } from '@evinced-private/ui-common';

import { SITE_SCANNER_APP_ID } from '../../../consts/dom-consts';
import { RoutesHelper } from '../../../helpers/RoutesHelper';
import { IProperty } from '../../../interfaces/Property';
import { isCrawlCurrentlyRunning } from '../../../services/PropertiesService';

import './PropertySettingsActions.scss';

interface IPropertySettingsActionsProps {
	property: IProperty;
	isPropertyInListMode: boolean;
	isSaveInProgress: boolean;
	onSaveChanges: (shouldCrawl?: boolean) => void;
	disableSaveButton?: boolean;
	showDone?: boolean;
	onDone?: () => void;
}

export const PropertySettingsActions: FC<IPropertySettingsActionsProps> = ({
	isPropertyInListMode,
	property,
	isSaveInProgress,
	onSaveChanges,
	disableSaveButton = false,
	showDone,
	onDone
}: IPropertySettingsActionsProps) => {
	const [isSaveAndCrawlConfirmPopupOpen, setSaveAndCrawlConfirmPopupOpen] = useState(false);
	const history = useHistory();
	const location = useLocation<{ from: string }>();

	const getBackUrl = (): string => {
		if (location?.state) {
			return location.state.from;
		}
		return RoutesHelper.getPropertiesPagePath();
	};

	const onCancel = (): void => {
		history.push(getBackUrl());
	};

	const onSave = (): void => {
		onSaveChanges();
	};

	const onSaveAndCrawlClick = async (): Promise<void> => {
		const isCrawlRunning = await isCrawlCurrentlyRunning(property.id);

		if (isCrawlRunning) {
			setSaveAndCrawlConfirmPopupOpen(true);
		} else {
			onSaveChanges(true);
		}
	};

	const onConfirmSaveAndCrawl = async (closeModal): Promise<void> => {
		onSaveChanges(true);
		setSaveAndCrawlConfirmPopupOpen(false);
		closeModal();
	};

	return (
		<div className="property-settings-actions">
			<EvButton
				type={BUTTON_TYPES.SECONDARY}
				title="Cancel"
				onClick={onCancel}
				className="property-settings-action-button"
			>
				Cancel
			</EvButton>
			{!showDone ? (
				<EvButton
					title="Save"
					loading={isSaveInProgress}
					onClick={onSave}
					disabled={disableSaveButton}
					className="property-settings-action-button"
				>
					Save
				</EvButton>
			) : (
				<EvButton title="Done" onClick={onDone} className="property-settings-action-button">
					Done
				</EvButton>
			)}
			{!isPropertyInListMode && (
				<>
					<EvButton title="Save & Crawl" loading={isSaveInProgress} onClick={onSaveAndCrawlClick}>
						Save & Crawl
					</EvButton>
					<EvConfirm
						appElement={SITE_SCANNER_APP_ID}
						title="Confirm Save and Crawl"
						approveButtonText="Confirm"
						promptMessage="Saving while a crawl is running will cancel the crawl, are you sure you want to save?"
						isOpen={isSaveAndCrawlConfirmPopupOpen}
						isControlled={true}
						onConfirm={onConfirmSaveAndCrawl}
						onClose={() => setSaveAndCrawlConfirmPopupOpen(false)}
					/>
				</>
			)}
		</div>
	);
};
